import { useState, useCallback } from 'react';

const VALID_QUESTION_TYPES = [
    'text', 'full_name', 'email', 'phone', 'number', 'url',
    'select', 'choices', 'yes_no_maybe', 'scale',
    'date', 'datetime', 'monetary', 'photo', 'video',
    'weight', 'gender', 'body_structure',
];

function validateTemplateJson(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return 'O arquivo não contém um objeto JSON válido.';
    }
    if (!data.title || typeof data.title !== 'string') {
        return 'Campo "title" ausente ou inválido.';
    }
    if (!Array.isArray(data.steps) || data.steps.length === 0) {
        return 'O formulário precisa ter ao menos uma etapa em "steps".';
    }
    for (const step of data.steps) {
        if (!step.title || typeof step.title !== 'string') {
            return 'Todas as etapas precisam ter um campo "title".';
        }
        if (!Array.isArray(step.questions) || step.questions.length === 0) {
            return `A etapa "${step.title}" precisa ter ao menos uma pergunta.`;
        }
        for (const q of step.questions) {
            if (!q.type || !VALID_QUESTION_TYPES.includes(q.type)) {
                return `Tipo de pergunta inválido: "${q.type}". Tipos aceitos: ${VALID_QUESTION_TYPES.join(', ')}.`;
            }
            if (!q.label || typeof q.label !== 'string') {
                return `Uma pergunta na etapa "${step.title}" não tem campo "label".`;
            }
        }
    }
    return null;
}

export default function useTemplatesModal({ onSelectTemplate }) {
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState(null);

    const processFile = useCallback((file) => {
        if (!file) return;
        if (!file.name.endsWith('.json') && file.type !== 'application/json') {
            setError('Apenas arquivos .json são aceitos.');
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                const validationError = validateTemplateJson(data);
                if (validationError) {
                    setError(validationError);
                    return;
                }
                setError(null);
                onSelectTemplate?.(data);
            } catch {
                setError('Não foi possível ler o arquivo. Verifique se é um JSON válido.');
            }
        };
        reader.readAsText(file);
    }, [onSelectTemplate]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        processFile(e.dataTransfer.files[0]);
    }, [processFile]);

    const handleFileInput = useCallback((e) => {
        processFile(e.target.files[0]);
        e.target.value = '';
    }, [processFile]);

    const clearError = useCallback(() => setError(null), []);

    return { dragOver, error, handleDragOver, handleDragLeave, handleDrop, handleFileInput, clearError };
}
